/**
 * @graphql cc
 */
'use no memo';
import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { styled, css } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';

import type { SizeType } from '@atlaskit/avatar/types';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import PageIcon from '@atlaskit/icon/core/page';

import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { TimeAgo, formatTimeAgo } from '@confluence/time-ago';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import type {
	CommentInlineResolveProperties,
	FooterResolvedProperties,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { SitePermissionTypeValues } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { AgentUserDualAvatar } from '@confluence/agent-user-dual-avatar/entry-points/AgentUserDualAvatar.ts';
import { getAgentBrand } from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import { useAgentAvatarColorMode } from '@confluence/agent-user-dual-avatar/entry-points/useAgentAvatarColorMode.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { RelativeTimestamp } from '@confluence/inline-comments-utils/entry-points/RelativeTimestamp.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import type {
	CommentAuthorInlineCommentAuthorQuery as CommentAuthorInlineCommentAuthorQuery$data,
	CommentAuthorInlineCommentAuthorQueryVariables as CommentAuthorInlineCommentAuthorQuery$variables,
	CommentAuthorInlineCommentAuthorQuery_content_PaginatedContentListWithChild_nodes_Content_history_History_createdBy_KnownUser as User,
} from './__types__/CommentAuthorInlineCommentAuthorQuery.ts';
import type {
	CommentAuthorUserQuery as CommentAuthorUserQuery$data,
	CommentAuthorUserQueryVariables as CommentAuthorUserQuery$variables,
} from './__types__/CommentAuthorUserQuery.ts';
import type { CommentContributor, CommentMode } from './inlineCommentsTypes.ts';
import { MaybeTooltip } from './MaybeTooltip.tsx';
import { useCommentAuthorQuery } from './useCommentAuthorQuery.ts';
const CommentAuthorInlineCommentAuthorQuery = graphql`
	query CommentAuthorInlineCommentAuthorQuery($commentId: ID!) {
		content(id: $commentId) {
			nodes {
				id
				history {
					createdBy {
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
				}
			}
		}
	}
`;
const CommentAuthorUserQuery = graphql`
	query CommentAuthorUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

type CommentAuthorProps = {
	commentMode: CommentMode;
	userId?: string | null;
	avatarUrl?: string | null;
	displayName?: string | null;
	date?: string | null;
	createdAtNonLocalized?: string | null;
	versionByDisplayName?: string | null;
	versionDate?: string | null;
	size?: SizeType;
	commentId?: string | null;
	permissionType?: SitePermissionType;
	isInactiveComment?: boolean;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	isCommentsPanel?: boolean;
	isGeneralComments?: boolean;
	isReply?: boolean;
	isEdited?: boolean;
	isFooterComment?: boolean;
	isStandaloneComment?: boolean;
	contributors?: ReadonlyArray<CommentContributor> | null;
};

type AuthorContainerProps = {
	commentMode: string;
	isStandaloneComment?: boolean;
	isFooterComment?: boolean;
	isReply?: boolean;
};

type DateContainerProps = {
	isStandaloneComment: boolean;
	isCommentsPanel?: boolean;
};

const getAuthorContainerMarginLeft = ({
	commentMode,
	isReply,
	isStandaloneComment,
	isFooterComment,
}: AuthorContainerProps) => {
	const negativeMarginForEditMode = '-16px';

	return commentMode === 'edit'
		? isReply || isStandaloneComment || isFooterComment
			? '0'
			: negativeMarginForEditMode
		: undefined;
};

const getAuthorContainerPaddingBottom = ({ commentMode, isReply }: AuthorContainerProps) => {
	return commentMode === 'edit' ? (isReply ? '8px' : '12px') : undefined;
};

const authorContainerBaseStyles = css({
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:visited, &:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text')} !important`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			'text-decoration': 'none !important',
		},
	},
});
const AuthorContainer: FC<
	AuthorContainerProps & {
		children: React.ReactNode;
	}
> = (props) => {
	const {
		commentMode: _commentMode,
		isReply: _isReply,
		isStandaloneComment: _isStandaloneComment,
		isFooterComment: _isFooterComment,
		...rest
	} = props;
	return (
		<div
			css={authorContainerBaseStyles}
			style={{
				marginLeft: getAuthorContainerMarginLeft(props),
				paddingBottom: getAuthorContainerPaddingBottom(props),
			}}
			{...rest}
		/>
	);
};

const avatarWrapperStyles = css({
	display: 'flex',
	position: 'relative',
	paddingRight: token('space.100'),
});

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	flex: '1 1 0%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:visited, &:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text')} !important`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span.comment-timestamp': {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		margin: 0,
		'background-color': 'transparent',
		border: 'none',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> a': {
			color: token('color.text.subtle'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'&:visited, &:hover': {
				color: token('color.text.subtle'),
			},
		},
	},
});

const Container: FC<{
	children: React.ReactNode;
	useProfileLinkStyle?: boolean;
	useColumnStyle?: boolean;
}> = ({ useProfileLinkStyle, useColumnStyle, ...props }) => {
	return (
		<div
			css={[
				containerStyles,
				useProfileLinkStyle && profileLinkContainerStyle,
				useColumnStyle && profileLinkContainerColumnStyle,
			]}
			{...props}
		/>
	);
};

const newPageIconStyle = xcss({
	position: 'relative',
});

const coAuthorPersonAvatarStyle = xcss({
	marginLeft: 'space.200',
	paddingLeft: 'space.025',
});

const coAuthorBotAvatarStyle = xcss({
	position: 'absolute',
	top: 'space.025',
	left: 'space.0',
});

const coAuthorCont = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: 'space.075',
});

const dualAvatarContainerStyle = xcss({
	position: 'relative',
	flexShrink: 0,
});

const profileLinkContainerStyle = css({
	flexDirection: 'row',
	alignItems: 'baseline',
	gap: token('space.100'),
	minWidth: 0,
	overflow: 'hidden',
	flex: '1 1 0%',
});

const profileLinkContainerColumnStyle = css({
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: 0,
});

const truncateTextStyle = {
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
	flex: '1 1 0%',
	wordBreak: 'unset',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProfileLinkContainer = styled(Container)({
	flexDirection: 'row',
	alignItems: 'center',
});

const singleLineHeaderStyles = css({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	alignItems: 'baseline',
	gap: token('space.100'),
	minWidth: 0,
});

const pageAttributionHeaderStyles = css({
	gap: token('space.050'),
});

const pageAttributionDateStyles = css({
	marginInlineStart: token('space.050'),
});

/**
 * Lets the text shrink past their content so a long name ellipsises (via the `Text``maxLines` clamp)
 */
const truncatableSlotStyles = css({
	minWidth: 0,
});

const nameAndDateRowStyles = css({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	alignItems: 'baseline',
	gap: token('space.100'),
	minWidth: 0,
});

const agentAttributionStyles = css({
	minWidth: 0,
	flex: '1 1 0%',
});

/**
 * Agent co-authored comments pull the timestamp up next to the agent name. In the page footer the
 * whole header fits on one line; general comments in the panel do too when page-comment agent
 * attribution spacing is enabled. In other comments-panel and sidebar cases, the `with {person}`
 * byline drops to a second line. Inline agentic comments use the attribution layout below.
 */
const CommentHeaderLayout: FC<{
	hasCoAuthor: boolean;
	isFooterComment: boolean;
	useInlineAttribution: boolean;
	usePageAttributionSpacing: boolean;
	authorName: React.ReactNode;
	byline: React.ReactNode;
	lozenge: React.ReactNode;
	dateInfo: React.ReactNode;
}> = ({
	hasCoAuthor,
	isFooterComment,
	useInlineAttribution,
	usePageAttributionSpacing,
	authorName,
	byline,
	lozenge,
	dateInfo,
}) => {
	if (useInlineAttribution) {
		return (
			<>
				<ProfileLinkContainer>
					<div css={agentAttributionStyles} data-testid="comment-author-attribution">
						{authorName}
						{byline && <> {byline}</>}
						{lozenge}
					</div>
				</ProfileLinkContainer>
				{dateInfo}
			</>
		);
	}

	if (hasCoAuthor && expValEquals('dual_agent_avatar_card', 'isEnabled', true)) {
		return isFooterComment || usePageAttributionSpacing ? (
			<ProfileLinkContainer>
				<div
					css={[singleLineHeaderStyles, usePageAttributionSpacing && pageAttributionHeaderStyles]}
					data-testid="comment-author-single-line-header"
				>
					<div css={truncatableSlotStyles}>{authorName}</div>
					{byline ? <div css={truncatableSlotStyles}>{byline}</div> : null}
					{lozenge}
					{usePageAttributionSpacing && dateInfo ? (
						<div css={pageAttributionDateStyles}>{dateInfo}</div>
					) : (
						dateInfo
					)}
				</div>
			</ProfileLinkContainer>
		) : (
			<ProfileLinkContainer>
				<div css={nameAndDateRowStyles} data-testid="comment-author-name-and-date">
					<div css={truncatableSlotStyles}>{authorName}</div>
					{dateInfo}
				</div>
				{byline}
				{lozenge}
			</ProfileLinkContainer>
		);
	}

	return (
		<>
			<ProfileLinkContainer>
				{authorName}
				{byline}
				{lozenge}
			</ProfileLinkContainer>
			{dateInfo}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	paddingLeft: `${token('space.100')}`,
	verticalAlign: 'top',
});

const dateContainerBaseStyles = css({
	display: 'flex',
	flexFlow: 'row nowrap',
	flexDirection: 'unset',
	alignItems: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		font: token('font.body.small'),
		margin: 0,
		backgroundColor: 'transparent',
		border: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> a': {
			color: token('color.text.subtle'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'&:visited, &:hover': {
				color: token('color.text.subtle'),
			},
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div button:focus': {
		boxShadow: `0 0 0 2px ${token('color.border.focused')}`,
	},
});

const dateContainerStandaloneStyles = css({
	flexDirection: 'column',
	alignItems: 'flex-start',
});

const dateContainerSubtlestStyles = css({
	color: token('color.text.subtlest'),
});
const DateContainer: FC<
	DateContainerProps & {
		children: React.ReactNode;
	}
> = ({ isStandaloneComment, isCommentsPanel, ...props }) => {
	return (
		<div
			css={[
				dateContainerBaseStyles,
				isStandaloneComment && dateContainerStandaloneStyles,
				(isStandaloneComment || isCommentsPanel) && dateContainerSubtlestStyles,
			]}
			{...props}
		/>
	);
};

// confluence_comments_cohiti_date_tooltips exp evaluations will use this component-level i18n object instead of commentsI18n
const i18n = defineMessages({
	defaultTooltip: {
		id: 'inline-comments-common.date-tooltip.default',
		defaultMessage: 'Added on {date} at {timestamp}',
		description: 'Tooltip to provide relative temporal context for when comment was added',
	},
	addedTooltip: {
		id: 'inline-comments-common.date-tooltip.added',
		defaultMessage: 'Added by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of author and relative temporal context for when comment was added',
	},
	resolvedTooltip: {
		id: 'inline-comments-common.date-tooltip.resolved',
		defaultMessage: 'Resolved by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of resolver and relative temporal context for when comment was resolved',
	},
	reopenedTooltip: {
		id: 'inline-comments-common.date-tooltip.reopened',
		defaultMessage: 'Reopened by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of re-opening user and relative temporal context for when comment was re-opened',
	},
	reopenedDateInfoText: {
		id: 'inline-comments-common.reopened.date.info.text',
		defaultMessage: `Reopened {timeAgo}{timeStamp, select,
			undefined {}
			other { at {timeStamp}}
		}`,
		description:
			'Info text on reopened comments describing when the comment was reopened, omitting timestamp if less than 24 hours have passed. For example: "Reopened May 3, 2024 at 1:04 PM" or "Reopened 5 minutes ago"',
	},
	resolvedDateInfoText: {
		id: 'inline-comments-common.resolved.date.info.text',
		defaultMessage: 'Resolved {timeAgo}',
		description:
			'Info text on resolved comments describing when the comment was resolved, for example: Resolved May 3, 2024',
	},
	coAuthorWithUser: {
		id: 'inline-comments-common.co-author.with-user',
		defaultMessage: 'with {username}',
		description:
			'Text shown next to the co-author name indicating who the human co-author is, for example: "with John Smith"',
	},
});

export const CommentAuthor: FC<CommentAuthorProps> = ({
	commentMode,
	userId,
	date,
	createdAtNonLocalized,
	size,
	displayName,
	avatarUrl,
	commentId,
	permissionType,
	isInactiveComment,
	resolveProperties,
	isCommentsPanel = false,
	isGeneralComments = false,
	isReply = false,
	isFooterComment = false,
	isStandaloneComment = false,
	contributors,
}) => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const colorMode = useAgentAvatarColorMode();
	const skipUserIdPath = Boolean((avatarUrl && displayName) || !userId || commentId);
	const { data: authorData } = useQueryInterop<
		CommentAuthorInlineCommentAuthorQuery$data,
		CommentAuthorInlineCommentAuthorQuery$variables
	>(CommentAuthorInlineCommentAuthorQuery, {
		variables: { commentId: commentId! },
		fetchPolicy: 'cache-and-network',
		skip: Boolean(userId || !commentId),
	});
	const { data: userDataFromOld } = useQueryInterop<
		CommentAuthorUserQuery$data,
		CommentAuthorUserQuery$variables
	>(CommentAuthorUserQuery, {
		variables: { id: userId! },
		fetchPolicy: 'cache-and-network',
		skip:
			skipUserIdPath || expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true),
	});
	const { data: userDataFromNew } = useCommentAuthorQuery(userId ?? undefined, {
		skip:
			skipUserIdPath || !expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true),
		fetchPolicy: 'cache-and-network',
	});
	const userData = expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true)
		? userDataFromNew
		: userDataFromOld;

	const author = (authorData?.content?.nodes || [])[0]?.history?.createdBy;
	const userAvatar = (
		author ? author.profilePicture?.path : (userData?.user?.photos || [])[0]?.value
	) as string | undefined;
	const userDisplayName = author ? author.displayName : userData?.user?.displayName;
	const userAccountId = (author as User)?.accountId;

	const intl = useIntl();
	const { formatMessage } = intl;
	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	const timeZone = userTimeZone || tenantTimeZone || undefined;

	const getTooltipContent = () => {
		if (
			resolveProperties?.resolvedTime &&
			expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
		) {
			// resolveProperties.resolved = true -> comment is in resolved state
			// resolveProperties.resolved = false -> comment is in re-opened state
			const i18nMessage = resolveProperties?.resolved ? i18n.resolvedTooltip : i18n.reopenedTooltip;
			const resolvedTimeBase = new Date(resolveProperties.resolvedTime);

			return formatMessage(i18nMessage, {
				username: resolveProperties?.resolvedUser,
				date: intl.formatDate(resolvedTimeBase, {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
					timeZone,
				}),
				timeStamp: intl.formatTime(resolvedTimeBase, {
					hour: 'numeric',
					minute: 'numeric',
					timeZone,
				}),
			});
		}

		if (resolveProperties?.resolved) {
			return formatMessage(commentsi18n.resolvedByTooltipTextCommentsPanel, {
				username: resolveProperties?.resolvedUser,
			});
		}
		return formatMessage(commentsi18n.reopenedByTooltipTextCommentsPanel, {
			username: resolveProperties?.resolvedUser,
			createdAtNonLocalized: <TimeAgo date={getValidDate(createdAtNonLocalized)} usePrepositions />,
		});
	};

	const getResolvedDateInfoText = () => {
		// resolveProperties.resolved = true -> comment is in resolved state
		// resolveProperties.resolved = false -> comment is in re-opened state
		const i18nKey = resolveProperties?.resolved ? 'resolvedDateInfoText' : 'reopenedDateInfoText';

		if (
			resolveProperties?.resolvedTime &&
			expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
		) {
			const resolvedTimeBase = new Date(resolveProperties.resolvedTime);

			return formatMessage(i18n[i18nKey], {
				// use formatTimeAgo directly so that this string can be injected into aria-label,
				timeAgo: formatTimeAgo(intl, resolvedTimeBase, true, {}, userTimeZone, tenantTimeZone),
				timeStamp: undefined,
			});
		} else {
			return formatMessage(commentsi18n[i18nKey], {
				friendlyDate: resolveProperties?.resolvedFriendlyDate,
			});
		}
	};

	const newExperienceResolveInfo = () => {
		return (
			<Tooltip position="top" content={getTooltipContent()}>
				<span aria-label={getResolvedDateInfoText()}>
					<Text color="color.text.subtlest" size="small" maxLines={2}>
						{getResolvedDateInfoText()}
					</Text>
				</span>
			</Tooltip>
		);
	};

	const hasPageCommentIconEnabled = useCallback(() => {
		if (!isGeneralComments) {
			return false;
		}

		return !isFooterComment;
	}, [isGeneralComments, isFooterComment]);

	const renderCopyButton = () => {
		return (
			<DateContainer isStandaloneComment={isStandaloneComment} isCommentsPanel={isCommentsPanel}>
				<span>
					<Inline space="space.050">
						{/* show resolve info if it exists, or creation date otherwise */}
						{resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate ? (
							newExperienceResolveInfo()
						) : (
							<Tooltip
								content={
									expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
										? formatMessage(i18n.defaultTooltip, {
												date: intl.formatDate(getValidDate(createdAtNonLocalized), {
													month: 'short',
													day: 'numeric',
													year: 'numeric',
													timeZone,
												}),
												timestamp: intl.formatTime(getValidDate(createdAtNonLocalized), {
													hour: 'numeric',
													minute: 'numeric',
													timeZone,
												}),
											})
										: undefined
								}
							>
								<RelativeTimestamp date={getValidDate(createdAtNonLocalized)} />
							</Tooltip>
						)}
						{hasPageCommentIconEnabled() && !isReply && (
							<Box xcss={newPageIconStyle}>
								<Tooltip content={formatMessage(commentsi18n.generalCommentTooltip)}>
									<PageIcon
										color={token('color.icon.subtlest')}
										testId="general-comment-page-icon"
										label={formatMessage(commentsi18n.generalCommentTooltip)}
									/>
								</Tooltip>
							</Box>
						)}
					</Inline>
				</span>
			</DateContainer>
		);
	};

	const humanAuthorName = displayName || userDisplayName;
	const name = humanAuthorName || '';

	const authorAccountId = userId || userAccountId;
	const coAuthor = contributors?.find((contributor) => {
		if (!contributor?.displayName) {
			return false;
		}
		const hasAccountIds = !!authorAccountId && contributor.accountId != null;
		const isCommentAuthor = hasAccountIds
			? contributor.accountId === authorAccountId
			: !!humanAuthorName && contributor.displayName === humanAuthorName;
		return !isCommentAuthor;
	});
	const coAuthorName = coAuthor?.displayName;
	// Recognised brands (Claude, Codex, Rovo, Cursor, ...) render a brand glyph instead of the
	// account's own avatar, and carry a canonical name so per-integration suffixes such as
	// "Claude Code (atlassian-mcp)" don't leak into the header. Under branded agent attribution
	// Identity sets the vendor name and avatar on the agent's own profile, so the co-author
	// renders from that profile and the brand table is not consulted.
	const coAuthorBrand =
		coAuthorName && !expValEquals('cc_identity_branded_agent_attribution', 'isEnabled', true)
			? getAgentBrand(coAuthorName)
			: undefined;
	const coAuthorAvatarUrl = coAuthor?.profilePicture?.path;
	const shouldShowCoAuthor = Boolean(coAuthorName && !renderServerPlaceholder);
	const shouldShowHumanAuthorByline = Boolean(shouldShowCoAuthor && humanAuthorName);
	const useInlineAttribution =
		shouldShowCoAuthor &&
		!isFooterComment &&
		expValEquals('dual_agent_avatar_card', 'isEnabled', true) &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true);
	const usePageAttributionSpacing =
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		shouldShowCoAuthor && isGeneralComments && fg('cc_page_comment_agent_attribution_spacing');

	return (
		<AuthorContainer
			commentMode={commentMode}
			isReply={isReply}
			isStandaloneComment={isStandaloneComment}
			isFooterComment={isFooterComment}
			data-testid="comment-author"
		>
			<div css={avatarWrapperStyles}>
				{shouldShowCoAuthor && expValEquals('dual_agent_avatar_card', 'isEnabled', true) ? (
					<Box xcss={dualAvatarContainerStyle}>
						<AgentUserDualAvatar
							agentName={coAuthorBrand?.name ?? coAuthorName ?? undefined}
							agentIcon={coAuthorBrand?.agentIcon}
							agentAvatarUrl={coAuthorAvatarUrl ?? undefined}
							agentBorderColor="transparent"
							agentUserId={coAuthor?.accountId ?? undefined}
							userName={humanAuthorName ?? undefined}
							userAvatarUrl={avatarUrl || userAvatar}
							relatedUserId={authorAccountId ?? undefined}
							colorMode={colorMode}
						/>
					</Box>
				) : (
					<>
						<Box xcss={shouldShowCoAuthor ? coAuthorPersonAvatarStyle : undefined}>
							<ProfileAvatar
								isBlockLevel
								userId={userId}
								src={avatarUrl || userAvatar}
								trigger="click"
								size={size}
								name={name}
							/>
						</Box>
						{shouldShowCoAuthor && (
							<Box xcss={coAuthorBotAvatarStyle}>
								{/* Agents are KnownUsers with a real aaid, so wrap the agent-shaped avatar in the
								    same profile-card trigger the human author uses — clicking it opens the profile
								    card (keyed off the agent's accountId), matching the human ProfileAvatar above. */}
								{/* The agent profile card (AgentCard) uses @atlaskit/flag's useFlags for its
								    favourite/delete actions, which throws without a FlagsProvider ancestor.
								    Confluence uses its own flag system, so provide the AK one around the card. */}
								<FlagsProvider>
									<ProfileCardWrapper
										userId={coAuthor?.accountId}
										trigger="click"
										fullName={coAuthorName ?? undefined}
										// Co-authors are Rovo agents (appType 'agent'); supplying agent config makes the
										// migrated profile card render the dedicated AgentCard instead of CardError.
										// (Ignored by the platform card when the subject isn't an agent.)
										agent={{}}
									>
										<AgentAvatar
											imageUrl={coAuthorAvatarUrl ?? undefined}
											agentIdentityAccountId={coAuthor?.accountId}
											size={size}
											name={coAuthorName ?? ''}
										/>
									</ProfileCardWrapper>
								</FlagsProvider>
							</Box>
						)}
					</>
				)}
			</div>
			<MaybeTooltip date={date} name={name}>
				<Container
					useProfileLinkStyle
					useColumnStyle={
						useInlineAttribution ||
						!!(resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate)
					}
				>
					<CommentHeaderLayout
						hasCoAuthor={shouldShowCoAuthor}
						isFooterComment={isFooterComment}
						useInlineAttribution={useInlineAttribution}
						usePageAttributionSpacing={usePageAttributionSpacing}
						dateInfo={date && renderCopyButton()}
						authorName={
							// When co-author (agent) present, show agent name as primary — no profile link since agents have no profile page
							isInactiveComment ? (
								<span data-testid="dummy-profile-link">{humanAuthorName}</span>
							) : shouldShowCoAuthor ? (
								useInlineAttribution ? (
									<Text weight="medium">{coAuthorBrand?.name ?? coAuthorName}</Text>
								) : (
									<Box xcss={coAuthorCont}>
										{expValEquals('dual_agent_avatar_card', 'isEnabled', true) ? (
											<Text weight="medium" maxLines={1}>
												{coAuthorBrand?.name ?? coAuthorName}
											</Text>
										) : (
											<Text weight="medium">{coAuthorName}</Text>
										)}
									</Box>
								)
							) : (
								<ProfileLink
									fullName={humanAuthorName!}
									userId={userId || userAccountId}
									style={{
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										color: token('color.text'),
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										fontWeight: token('font.weight.medium'),
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										...truncateTextStyle,
									}}
									isUnderlinedByDefault={false}
								/>
							)
						}
						byline={
							shouldShowHumanAuthorByline && (
								<Text
									color="color.text.subtlest"
									size="small"
									maxLines={
										!useInlineAttribution &&
										expValEquals('dual_agent_avatar_card', 'isEnabled', true)
											? 1
											: undefined
									}
								>
									{formatMessage(i18n.coAuthorWithUser, {
										username: humanAuthorName,
									})}
								</Text>
							)
						}
						lozenge={
							permissionType === SitePermissionTypeValues.EXTERNAL ? (
								<LozengeWrapper>
									<ExternalCollaboratorUserLozenge />
								</LozengeWrapper>
							) : null
						}
					/>
				</Container>
			</MaybeTooltip>
		</AuthorContainer>
	);
};
